import {Component, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import {
    AbstractStylizableComponent,
    APP_NAME,
    ColorizableComponent,
    ColorizableTextComponent,
    EnvironmentProvider,
    ExtendedWorkspace,
    PesAppFooterStyleRule,
    PesStyle,
    PesStyleService,
    PesStyleType,
    SmartUIService,
    Strings,
    SubscriptionManager
} from '@pes/ui';
import {ISubscription} from 'rxjs/Subscription';

@Component({
    selector: 'lbpa-footer',
    templateUrl: './lbpa-footer.component.html'
})
export class LbpaFooterComponent extends AbstractStylizableComponent<PesAppFooterStyleRule>
    implements ColorizableComponent, ColorizableTextComponent, OnInit, OnDestroy {

    /**
     * Indicates whether to display the app name (true), or not (false).
     */
    @Input() public showAppName: boolean = true;

    /**
     * Indicates whether to display the app version (true), or not (false).
     */
    @Input() public showAppVersion: boolean = true;

    /**
     * Indicates whether to display the PES-UI version (true), or not (false).
     */
    @Input() public showPesUiVersion: boolean = true;

    /**
     * Indicates whether this footer component is the app footer (true), or not (false).
     */
    @Input() public isAppFooter: boolean = true;

    @Input() public showFooter: boolean = false;

    /**
     * The name of the application.
     */
    protected appName: string = Strings.EMPTY_STRING;
    /**
     * The style object used by the ngStyle directive to set custom styles.
     */
    protected customStyles: any = null;
    /**
     * The reference to the EnvironmentProvider service.
     */
    protected env: EnvironmentProvider = null;
    /**
     * The options of the smartUI config.
     */
    protected extendedWorkspace: ExtendedWorkspace = null;
    /**
     * The reference to the PesStyleService service.
     */
    private readonly _styleService: PesStyleService = null;
    /**
     * Reference of PES smartUI service.
     */
    private readonly _smartUIService: SmartUIService = null;

    /**
     * Creates a new PesFooterComponent instance.
     *
     * @param {Injector} injector the reference to the angular injector.
     */
    constructor(protected injector: Injector) {
        super();
        this.env = injector.get(EnvironmentProvider);
        this._styleService = injector.get(PesStyleService);
        this.appName = injector.get(APP_NAME);
        this._smartUIService = injector.get(SmartUIService);
        SubscriptionManager.getInstance().createSubscribable(this);
        this.extendedWorkspace = this._smartUIService.extendedWorkspace;
        const sub: ISubscription = this._smartUIService.onExtendedChange.subscribe((options: ExtendedWorkspace) => {
            if (this.extendedWorkspace !== options) {
                this.extendedWorkspace = options;
            }
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * The background color for this component.
     */
    private _backgroundColor: any = null;

    public get backgroundColor(): any {
        return this._backgroundColor;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set backgroundColor(value: any) {
        this._backgroundColor = value;
        this.initStyles();
    }

    /**
     * The text color for this component.
     */
    private _color: any = null;

    public get color(): any {
        return this._color;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set color(value: any) {
        this._color = value;
        this.initStyles();
    }

    /**
     * The text size for this component.
     */
    private _fontSize: string = null;

    public get fontSize(): string {
        return this._fontSize;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set fontSize(value: string) {
        this._fontSize = value;
        this.initStyles();
    }

    /**
     * The text weight for this component.
     */
    private _fontWeight: string = null;

    public get fontWeight(): string {
        return this._fontWeight;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set fontWeight(value: string) {
        this._fontWeight = value;
        this.initStyles();
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set style(value: PesStyle) {
        if (value) {
            const rules: PesAppFooterStyleRule = value.rules;
            this._color = rules.color;
            this._fontSize = rules.fontSize;
            this._fontWeight = rules.fontWeight;
            this._backgroundColor = rules.backgroundColor;
        }
        this.initPesStyle(value);
    }

    /**
     * @inheritDoc
     */
    public ngOnInit(): void {
        const sub: ISubscription = this._styleService.styleChanged.subscribe(this.checkStyles.bind(this));
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * @inheritDoc
     */
    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }

    /**
     * @inheritDoc
     */
    protected initStyles(): void {
        this.customStyles = {};
        this.setStyle(this.customStyles, 'color', this.color);
        this.setStyle(this.customStyles, 'background-color', this.backgroundColor);
        this.setStyle(this.customStyles, 'font-weight', this.fontWeight);
        this.setStyle(this.customStyles, 'font-size', this.fontSize);
    }

    /**
     * Initializes the PES styles for this component.
     *
     * @param {PesStyle[]} styles the list of PES styles for this component.
     */
    private checkStyles(styles: PesStyle[]): void {
        if (styles) {
            this.style = styles.find((elm: PesStyle) => {
                return elm.type === PesStyleType.FOOTER;
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/component/lbpa-footer/lbpa-footer.component.ts